<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import CoLivingTabs from './CoLivingTabs.vue';
import RuleModal from './components/RuleModal.vue';
import OpsRoleCard from './components/OpsRoleCard.vue';
import OutboundInboxCard from './components/OutboundInboxCard.vue';
import ResolverPanel from './components/ResolverPanel.vue';
import { apiErrorMessage } from '../../../api/coLiving/errors';
import { CARD_CLASS } from './constants';
import { TIER_STYLE } from './helpers';

export default {
  name: 'CoLivingRoutingPage',
  components: {
    PageHeader,
    LoadingState,
    DeleteModal,
    CoLivingTabs,
    RuleModal,
    OpsRoleCard,
    OutboundInboxCard,
    ResolverPanel,
  },
  data() {
    return {
      cardClass: CARD_CLASS,
      tierStyle: TIER_STYLE,
      loadError: '',
      modalKind: '',
      editingRule: null,
      ruleToDelete: null,
      deleteKind: '',
      isDeleting: false,
    };
  },
  computed: {
    ...mapGetters('coLivingRouting', {
      defaults: 'getDefaults',
      overrides: 'getOverrides',
      uiFlags: 'getUiFlags',
    }),
    ...mapGetters('coLivingCatalog', { roles: 'getRoles' }),
  },
  mounted() {
    this.load();
  },
  methods: {
    ...mapActions('coLivingRouting', [
      'fetchRules',
      'removeDefault',
      'removeOverride',
    ]),
    ...mapActions('coLivingCatalog', ['fetchRoles', 'fetchCategories']),
    ...mapActions('coLivingCondos', ['fetchCondos']),
    ...mapActions('coLivingConfig', ['fetchSettings']),
    async load() {
      this.loadError = '';
      try {
        await Promise.all([
          this.fetchRules(),
          this.fetchRoles(),
          this.fetchCategories(),
          this.fetchCondos(),
          this.fetchSettings(),
        ]);
      } catch (err) {
        this.loadError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.COMMON.LOAD_FAILED'
        );
      }
    },
    roleLabel(key) {
      if (!key) return '';
      const role = this.roles.find(r => r.key === key);
      return role ? role.label : key;
    },
    openAdd(kind) {
      this.editingRule = null;
      this.modalKind = kind;
    },
    openEdit(kind, rule) {
      this.editingRule = rule;
      this.modalKind = kind;
    },
    askDelete(kind, rule) {
      this.deleteKind = kind;
      this.ruleToDelete = rule;
    },
    async confirmDelete() {
      this.isDeleting = true;
      try {
        if (this.deleteKind === 'override') {
          await this.removeOverride(this.ruleToDelete.id);
        } else {
          await this.removeDefault(this.ruleToDelete.id);
        }
        useAlert(this.$t('CO_LIVING.ROUTING.DELETED'));
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'CO_LIVING.ROUTING.DELETE_FAILED'));
      } finally {
        this.isDeleting = false;
        this.ruleToDelete = null;
      }
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-auto p-6" data-testid="co-living-routing-page">
    <PageHeader
      :title="$t('CO_LIVING.TITLE')"
      icon="condo"
      :description="$t('CO_LIVING.DESCRIPTION')"
    />
    <CoLivingTabs />

    <LoadingState
      v-if="uiFlags.isFetching && !defaults.length"
      :message="$t('CO_LIVING.COMMON.LOADING')"
    />
    <p
      v-else-if="loadError"
      class="text-sm text-red-500"
      data-testid="co-living-load-error"
    >
      {{ loadError }}
    </p>

    <div v-else class="flex flex-col gap-6">
      <p class="text-sm text-slate-600 dark:text-slate-300">
        {{ $t('CO_LIVING.ROUTING.INTRO') }}
      </p>

      <section :class="cardClass" data-testid="co-living-defaults">
        <div class="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 class="text-lg font-medium text-slate-900 dark:text-slate-100">
              {{ $t('CO_LIVING.ROUTING.DEFAULTS_TITLE') }}
            </h2>
            <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {{ $t('CO_LIVING.ROUTING.DEFAULTS_DESCRIPTION') }}
            </p>
          </div>
          <woot-button
            icon="add"
            data-testid="co-living-add-default"
            @click="openAdd('default')"
          >
            {{ $t('CO_LIVING.ROUTING.DEFAULT_ADD') }}
          </woot-button>
        </div>
        <p
          v-if="!defaults.length"
          class="text-sm text-slate-500 dark:text-slate-400"
          data-testid="co-living-defaults-empty"
        >
          {{ $t('CO_LIVING.ROUTING.DEFAULTS_EMPTY') }}
        </p>
        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm" data-testid="co-living-defaults-table">
            <thead>
              <tr class="text-left text-slate-500 dark:text-slate-400">
                <th class="p-2">
                  {{ $t('CO_LIVING.ROUTING.COLUMNS.CATEGORY') }}
                </th>
                <th class="p-2">
                  {{ $t('CO_LIVING.ROUTING.COLUMNS.PRIMARY') }}
                </th>
                <th class="p-2">
                  {{ $t('CO_LIVING.ROUTING.COLUMNS.BACKUP') }}
                </th>
                <th class="p-2">
                  {{ $t('CO_LIVING.ROUTING.COLUMNS.SUPERVISOR') }}
                </th>
                <th class="p-2">{{ $t('CO_LIVING.ROUTING.COLUMNS.TIER') }}</th>
                <th class="p-2" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="rule in defaults"
                :key="rule.id"
                class="border-t border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"
                :data-testid="`co-living-default-row-${rule.category}`"
              >
                <td class="p-2">{{ rule.category_name }}</td>
                <td class="p-2">{{ roleLabel(rule.primary_role) }}</td>
                <td class="p-2">{{ roleLabel(rule.backup_role) || '-' }}</td>
                <td class="p-2">
                  {{ roleLabel(rule.supervisor_role) || '-' }}
                </td>
                <td class="p-2">
                  <span
                    v-if="rule.sla_tier"
                    class="px-2 py-0.5 text-xs font-medium rounded"
                    :class="tierStyle[rule.sla_tier]"
                  >
                    {{ rule.sla_tier }}
                  </span>
                  <span v-else class="text-xs text-slate-500">
                    {{ $t('CO_LIVING.ROUTING.TIER_FROM_CATEGORY') }}
                  </span>
                </td>
                <td class="p-2 text-right whitespace-nowrap">
                  <woot-button
                    v-tooltip.top="$t('CO_LIVING.COMMON.EDIT')"
                    variant="clear"
                    size="small"
                    icon="edit"
                    :data-testid="`co-living-default-edit-${rule.category}`"
                    :aria-label="$t('CO_LIVING.COMMON.EDIT')"
                    @click="openEdit('default', rule)"
                  />
                  <woot-button
                    v-tooltip.top="$t('CO_LIVING.COMMON.DELETE')"
                    variant="clear"
                    size="small"
                    color-scheme="alert"
                    icon="delete"
                    :data-testid="`co-living-default-delete-${rule.category}`"
                    :aria-label="$t('CO_LIVING.COMMON.DELETE')"
                    @click="askDelete('default', rule)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section :class="cardClass" data-testid="co-living-overrides">
        <div class="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 class="text-lg font-medium text-slate-900 dark:text-slate-100">
              {{ $t('CO_LIVING.ROUTING.OVERRIDES_TITLE') }}
            </h2>
            <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {{ $t('CO_LIVING.ROUTING.OVERRIDES_DESCRIPTION') }}
            </p>
          </div>
          <woot-button
            icon="add"
            data-testid="co-living-add-override"
            @click="openAdd('override')"
          >
            {{ $t('CO_LIVING.ROUTING.OVERRIDE_ADD') }}
          </woot-button>
        </div>
        <p
          v-if="!overrides.length"
          class="text-sm text-slate-500 dark:text-slate-400"
          data-testid="co-living-overrides-empty"
        >
          {{ $t('CO_LIVING.ROUTING.OVERRIDES_EMPTY') }}
        </p>
        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm" data-testid="co-living-overrides-table">
            <thead>
              <tr class="text-left text-slate-500 dark:text-slate-400">
                <th class="p-2">{{ $t('CO_LIVING.ROUTING.COLUMNS.CONDO') }}</th>
                <th class="p-2">
                  {{ $t('CO_LIVING.ROUTING.COLUMNS.CATEGORY') }}
                </th>
                <th class="p-2">
                  {{ $t('CO_LIVING.ROUTING.COLUMNS.PRIMARY') }}
                </th>
                <th class="p-2">
                  {{ $t('CO_LIVING.ROUTING.COLUMNS.BACKUP') }}
                </th>
                <th class="p-2">
                  {{ $t('CO_LIVING.ROUTING.COLUMNS.SUPERVISOR') }}
                </th>
                <th class="p-2">{{ $t('CO_LIVING.ROUTING.COLUMNS.TIER') }}</th>
                <th class="p-2" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="rule in overrides"
                :key="rule.id"
                class="border-t border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"
                :data-testid="`co-living-override-row-${rule.id}`"
              >
                <td class="p-2">{{ rule.site_name }}</td>
                <td class="p-2">{{ rule.category_name }}</td>
                <td class="p-2">{{ roleLabel(rule.primary_role) }}</td>
                <td class="p-2">
                  {{
                    roleLabel(rule.backup_role) ||
                    $t('CO_LIVING.ROUTING.INHERIT')
                  }}
                </td>
                <td class="p-2">
                  {{
                    roleLabel(rule.supervisor_role) ||
                    $t('CO_LIVING.ROUTING.INHERIT')
                  }}
                </td>
                <td class="p-2">
                  {{ rule.sla_tier || $t('CO_LIVING.ROUTING.INHERIT') }}
                </td>
                <td class="p-2 text-right whitespace-nowrap">
                  <woot-button
                    v-tooltip.top="$t('CO_LIVING.COMMON.EDIT')"
                    variant="clear"
                    size="small"
                    icon="edit"
                    :data-testid="`co-living-override-edit-${rule.id}`"
                    :aria-label="$t('CO_LIVING.COMMON.EDIT')"
                    @click="openEdit('override', rule)"
                  />
                  <woot-button
                    v-tooltip.top="$t('CO_LIVING.COMMON.DELETE')"
                    variant="clear"
                    size="small"
                    color-scheme="alert"
                    icon="delete"
                    :data-testid="`co-living-override-delete-${rule.id}`"
                    :aria-label="$t('CO_LIVING.COMMON.DELETE')"
                    @click="askDelete('override', rule)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <ResolverPanel />
      <OpsRoleCard />
      <OutboundInboxCard />
    </div>

    <RuleModal
      v-if="modalKind"
      :kind="modalKind"
      :rule="editingRule"
      @close="modalKind = ''"
    />
    <DeleteModal
      v-if="ruleToDelete"
      :delete-name="ruleToDelete.category_name"
      :is-loading="isDeleting"
      @close="ruleToDelete = null"
      @confirm="confirmDelete"
    />
  </div>
</template>
